import React from 'react';
import PropTypes from 'prop-types';

import PriceLabel from '@prisjakt/ui-price-label';
import Rating from '@prisjakt/ui-rating';
import CartIcon from 'mdi-react/CartIcon';

import * as S from './styles';

const propertyCellWidth = propertyCount => {
  if (propertyCount >= 4) {
    return 'narrowest';
  }
  if (propertyCount === 3) {
    return 'narrower';
  }
  return 'narrow';
};

const ProductListItem = props => {
  const selectAction = e => {
    e.preventDefault();
    if (props.selected) {
      props.onDeselect();
    } else {
      props.onSelect();
    }
  };
  const linkProps = {
    ...props.linkAttributes,
    onClick: e => {
      if (props.selectable) {
        selectAction(e);
        return;
      }

      // prevent internal routing and force window.replace
      if (props.externalLink) {
        e.preventDefault();
        window.location.href = e.currentTarget.href;
      }

      props.onClick(e);
    },
    to: props.link,
  };

  return (
    <S.TableRow as={props.as} selectable={props.selectable}>
      {props.selectable && (
        <S.SelectBoxCell>
          <S.SelectBox
            vertical
            selected={props.selected}
            onSelect={props.onSelect}
            onDeselect={props.onDeselect}
            aria-label={props.name}
          />
        </S.SelectBoxCell>
      )}
      {props.image && (
        <S.ImageWrapper {...linkProps} tabIndex="-1">
          <S.Image>{props.image}</S.Image>
        </S.ImageWrapper>
      )}

      <S.BasicInfo
        hasExtra={!!props.campaignBadge}
        {...linkProps}
        aria-label={props.name}
      >
        <S.ProductDetails>
          <S.ProductName>{props.name}</S.ProductName>

          {props.rating && (
            <Rating
              hide={props.rating === null}
              rate={props.rating}
              showRate
              size="small"
              count={props.count}
              countTranslation={props.countTranslation}
              showCounter
            />
          )}

          {props.subtitle && <S.Subtitle>{props.subtitle}</S.Subtitle>}
        </S.ProductDetails>

        <S.BadgeArea>
          {props.campaignBadge && (
            <S.CampaignBadge>{props.campaignBadge}</S.CampaignBadge>
          )}
        </S.BadgeArea>
      </S.BasicInfo>

      {props.price && (
        <S.PriceLabelWrapper {...linkProps} tabIndex="-1">
          <PriceLabel value={props.price} />
          {props.storesCount !== null && (
            <S.StoreCount title={props.storesCountTranslation}>
              <span>{props.storesCount}</span> <CartIcon height={16} />
            </S.StoreCount>
          )}
        </S.PriceLabelWrapper>
      )}

      {props.properties &&
        props.properties.length > 0 &&
        props.properties.map((prop, index) => (
          <S.PropertyCell
            {...linkProps}
            tabIndex="-1"
            key={index.toString()}
            width={propertyCellWidth(props.properties.length)}
          >
            {prop}
          </S.PropertyCell>
        ))}
    </S.TableRow>
  );
};

const {
  func,
  arrayOf,
  object,
  string,
  number,
  oneOfType,
  element,
  node,
  shape,
  bool,
  elementType,
} = PropTypes;

ProductListItem.propTypes = {
  onClick: func,
  link: string.isRequired,
  image: node,
  name: string.isRequired,
  rating: number,
  count: number,
  countTranslation: shape({
    domain: string,
    key: string,
  }),
  price: oneOfType([string, number, element]),
  campaignBadge: element,
  subtitle: node,
  properties: oneOfType([arrayOf(string), arrayOf(object)]),
  linkAttributes: shape({
    target: string,
    rel: string,
  }),
  storesCount: number,
  storesCountTranslation: string,
  selectable: bool,
  selected: bool,
  onSelect: func,
  onDeselect: func,
  as: elementType,
  externalLink: bool,
};

const noop = () => {};

ProductListItem.defaultProps = {
  onClick: noop,
  image: null,
  rating: undefined,
  count: undefined,
  countTranslation: {
    domain: 'product',
    key: 'product-card.n-ratings',
  },
  price: undefined,
  campaignBadge: null,
  subtitle: undefined,
  properties: [],
  linkAttributes: {},
  storesCount: null,
  storesCountTranslation: '',
  selectable: false,
  selected: false,
  onSelect: noop,
  onDeselect: noop,
  as: 'div',
  externalLink: false,
};

export default ProductListItem;
